import { useState, useCallback, useEffect } from "react";
import {
  Outlet
  // useLocation
} from "react-router";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { SyncingBanner } from "@/components/layout/SyncingBanner";
//import { PromotionalBanner } from "@/components/layout/PromotionalBanner";
//import { useBanner } from "@/hooks/useBanner";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { CopilotKitCSSProperties } from "@copilotkit/react-ui";
import { useConfigStore } from "@/store/useConfigStore";
import { OnboardingSurveyPopup } from "@/components/onboarding/OnboardingSurveyPopup";
import { useOnboardingSurvey } from "@/hooks/useOnboardingSurvey";
import "./Mainlayout.css";

export default function MainLayout() {
  //const location = useLocation();
  //const currentPage =
  //  location.pathname.split("/").filter(Boolean)[0] || "Dashboard";
  // const pageTitle = currentPage.charAt(0).toUpperCase() + currentPage.slice(1);
  const { config } = useConfigStore();
  const organizationStatus = useOrganizationStore(
    (state) => state.organization.status
  );
  //const { activeBanners, dismissBanner } = useBanner();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMainSidebarCollapsed, setIsMainSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Onboarding survey popup
  const { showSurvey, surveyData, saveSurveyData, closeSurvey } =
    useOnboardingSurvey();

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // Allow other parts of the app (e.g., Dashboard Ask Emma) to open the AI chat
  useEffect(() => {
    const openEmmaListener = () => setIsSidebarOpen(true);
    window.addEventListener("openEmmaChat", openEmmaListener);
    return () => window.removeEventListener("openEmmaChat", openEmmaListener);
  }, []);

  const toggleMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  // Automatically collapse the main sidebar when the right sidebar is opened
  useEffect(() => {
    if (isSidebarOpen) {
      setIsMainSidebarCollapsed(true);
    } else {
      // Restore the main sidebar when the right sidebar is closed
      setIsMainSidebarCollapsed(false);
    }
  }, [isSidebarOpen]);

  // Close mobile sidebar when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 800) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      style={
        {
          "--copilot-kit-primary-color": config.primaryColor,
          "--copilot-kit-secondary-color": config.secondaryColor,
          "--copilot-kit-background-color": " rgb(239 246 255)",
          "--copilot-kit-border": "1px solid rgb(219 234 254)",
          "--copilot-kit-border-radius": "0.5rem",
          "--copilot-kit-box-shadow": "0 1px 2px 0 rgb(0 0 0 / 0.05)"
        } as CopilotKitCSSProperties
      }
    >
      {/* Onboarding Survey Popup - One-time display */}
      <OnboardingSurveyPopup
        open={showSurvey}
        onClose={closeSurvey}
        onStepComplete={saveSurveyData}
        initialData={surveyData}
      />

      <div className="flex h-screen">
        {/* Mobile Sidebar Overlay */}
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={toggleMobileSidebar}
          />
        )}

        {/* Sidebar - Hidden on mobile by default, shown when toggled */}
        <div
          className={`
          fixed lg:relative z-50 h-full bg-white
          ${isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          transition-transform duration-300 ease-in-out
          shadow-lg lg:shadow-none
        `}
        >
          <Sidebar
            isCollapsed={isMainSidebarCollapsed}
            onCollapseChange={setIsMainSidebarCollapsed}
            onMobileToggle={toggleMobileSidebar}
          />
        </div>

        <div
          className={`flex flex-1 flex-col overflow-y-auto transition-all duration-300 ${isSidebarOpen ? "mr-96" : ""}`}
        >
          <Header
            isSidebarOpen={isSidebarOpen}
            onToggleSidebar={toggleSidebar}
            onToggleMobileSidebar={toggleMobileSidebar}
          />
          <SyncingBanner status={organizationStatus} />

          {/* Promotional Banner */}
          {/* {activeBanners.length > 0 && (
            <div className="w-full bg-white border-b border-gray-200">
              <PromotionalBanner
                banners={activeBanners}
                onDismiss={dismissBanner}
                className="max-w-full"
                autoSlide={true}
                slideInterval={5000}
              />
            </div>
          )} */}

          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
